/**
 * Welcome — the first thing a new customer sees after signing up.
 *
 *   1. Your business   name, type, what you offer, contact facts, web address
 *   2. Your website    description, pages, look & feel
 *   3. Ready           credits check → Build my website
 *
 * Answers are saved to the project as the customer goes, so leaving and
 * coming back (or the Studio page) picks up where they stopped. On "Build"
 * we start the run and land them on the live progress view.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Coins, Loader2, Sparkles } from 'lucide-react';
import { apiHelpers, studioHelpers, type StudioIntake } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import {
  BriefForm,
  briefIsReady,
  emptyBrief,
  type BriefFormValue,
} from '@/components/studio/BriefForm';
import { TopUpDialog, useAiCredits } from '@/components/studio/AiCreditsCard';

type Step = 0 | 1 | 2;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [step, setStep] = useState<Step>(0);
  const [value, setValue] = useState<BriefFormValue>(emptyBrief());
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [topUp, setTopUp] = useState(false);

  // The team's project (one per team).
  const projectsQ = useQuery({
    queryKey: qk.myProject(),
    queryFn: async () => {
      const res = await apiHelpers.getProjects({ limit: 1 });
      return (res.data?.data?.[0] ?? null) as { id: string; name: string; slug: string } | null;
    },
  });
  const project = projectsQ.data;
  const overviewQ = useQuery({
    queryKey: qk.studio(project?.id),
    queryFn: () => studioHelpers.overview(project!.id),
    enabled: Boolean(project?.id),
  });
  const credits = useAiCredits();

  // Seed the form from anything already saved.
  useEffect(() => {
    const ov = overviewQ.data;
    if (!ov) return;
    if (ov.activeRun || ov.project.hasSite) {
      navigate(`/projects/${ov.project.id}/website`, { replace: true });
      return;
    }
    setValue((v) =>
      v.intake.businessName
        ? v
        : { intake: { pages: [], ...(ov.project.intake ?? {}) }, prompt: v.prompt }
    );
    setSlug((s) => s || ov.project.slug);
  }, [overviewQ.data, navigate]);

  useEffect(() => {
    if (!slugTouched && value.intake.businessName) setSlug(slugify(value.intake.businessName));
  }, [value.intake.businessName, slugTouched]);

  const save = useMutation({
    mutationFn: (body: {
      name?: string;
      slug?: string;
      intake?: StudioIntake;
      onboardingCompleted?: boolean;
    }) => studioHelpers.updateSettings(project!.id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.studio(project?.id) }),
  });

  const start = useMutation({
    mutationFn: () =>
      studioHelpers.startRun(project!.id, {
        kind: 'generate',
        prompt: value.prompt,
        intake: value.intake,
      }),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: qk.studio(project?.id) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      navigate(`/projects/${project!.id}/website?run=${run.id}`, { replace: true });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data
        ?.error?.code;
      if (code === 'AI_CREDITS_REQUIRED') {
        setTopUp(true);
        return;
      }
      toast.error(apiErrorMessage(err, 'Could not start building'));
    },
  });

  const businessReady =
    Boolean(value.intake.businessName?.trim()) && Boolean(value.intake.archetype);
  const ready = briefIsReady(value);
  const needed =
    overviewQ.data?.credits.reserveFor.generate ?? credits.data?.reserveFor.generate ?? 0;
  const available = credits.data?.available ?? overviewQ.data?.credits.available ?? 0;
  const enough = available >= needed;
  // The Studio page shows an honest banner when the runner is not configured;
  // the wizard must not end in a dead "Build" click for the same state.
  const runnerAvailable = overviewQ.data?.runnerAvailable ?? true;

  const next = async () => {
    if (!project) return;
    if (step === 0) {
      try {
        await save.mutateAsync({
          name: value.intake.businessName?.trim() || undefined,
          slug: slug && slug !== project.slug ? slug : undefined,
          intake: value.intake,
        });
        setStep(1);
      } catch (err) {
        toast.error(apiErrorMessage(err, 'Could not save'));
      }
      return;
    }
    if (step === 1) {
      await save.mutateAsync({ intake: value.intake }).catch(() => undefined);
      setStep(2);
    }
  };

  const steps = useMemo(() => ['Your business', 'Your website', 'Build'], []);

  if (projectsQ.isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!project) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className="text-xl font-semibold">Almost there</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Your account isn't attached to a website yet. Ask your team owner for an invite, or
          contact support.
        </p>
        <Button className="mt-6" onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-8 text-center">
          <p className="text-primary flex items-center justify-center gap-2 text-sm font-medium">
            <Sparkles className="h-4 w-4" /> Welcome{user?.firstName ? `, ${user.firstName}` : ''}
          </p>
          <h1 className="mt-2 text-3xl font-bold">Let's build your website</h1>
          <p className="text-muted-foreground mt-2">
            Three quick steps. Tell us about your business, describe the site you want, and we'll
            build it while you watch.
          </p>
        </div>

        <ol className="mb-8 flex items-center justify-center gap-3 text-sm">
          {steps.map((s, i) => (
            <li key={s} className="flex items-center gap-3">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${
                  i < step
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : i === step
                      ? 'border-primary text-primary'
                      : 'text-muted-foreground'
                }`}
              >
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={i === step ? 'font-medium' : 'text-muted-foreground'}>{s}</span>
              {i < steps.length - 1 && <span className="bg-border h-px w-8" />}
            </li>
          ))}
        </ol>

        <div className="bg-card rounded-2xl border p-6 shadow-sm sm:p-8">
          {step === 0 && (
            <div className="space-y-6">
              <BriefForm value={value} onChange={setValue} sections={['business']} />
              <div>
                <label className="mb-1 block text-sm font-medium">Your web address</label>
                <div className="flex items-center gap-0 rounded-lg border">
                  <input
                    className="bg-background w-full rounded-l-lg px-3 py-2 text-sm focus:outline-none"
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(slugify(e.target.value));
                    }}
                    placeholder="your-business"
                  />
                  <span className="text-muted-foreground bg-muted rounded-r-lg px-3 py-2 text-sm">
                    .sites.tyashin.com
                  </span>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Your site goes live here first. You can connect your own domain any time later.
                </p>
              </div>
            </div>
          )}
          {step === 1 && <BriefForm value={value} onChange={setValue} sections={['website']} />}
          {step === 2 && (
            <div className="space-y-6">
              <div className="rounded-xl border p-5">
                <p className="font-semibold">{value.intake.businessName}</p>
                <p className="text-muted-foreground text-sm">{slug}.sites.tyashin.com</p>
                <p className="mt-3 text-sm">{value.prompt}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(value.intake.pages ?? []).map((p) => (
                    <span key={p} className="bg-muted rounded-full px-2.5 py-0.5 text-xs">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              {!runnerAvailable && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
                  <p className="font-medium">Website building is temporarily unavailable.</p>
                  <p className="text-muted-foreground mt-1">
                    Your brief is saved. Come back shortly and press Build — nothing is lost.
                  </p>
                </div>
              )}
              <div
                className={`flex items-start gap-3 rounded-xl border p-4 ${enough ? '' : 'border-amber-300 bg-amber-50'}`}
              >
                <Coins
                  className={`mt-0.5 h-5 w-5 ${enough ? 'text-amber-500' : 'text-amber-600'}`}
                />
                <div className="text-sm">
                  <p className="font-medium">
                    {enough
                      ? `You have ${available.toLocaleString()} AI credits — enough to start.`
                      : `Building needs ${needed.toLocaleString()} AI credits to start; you have ${available.toLocaleString()}.`}
                  </p>
                  <p className="text-muted-foreground mt-1">
                    You're charged only for what the build actually uses. It usually takes 10–25
                    minutes and you'll see every step.
                  </p>
                  {!enough && (
                    <Button size="sm" className="mt-3" onClick={() => setTopUp(true)}>
                      Top up credits
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="ghost"
              disabled={step === 0}
              onClick={() => setStep((s) => (s > 0 ? ((s - 1) as Step) : s))}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {step < 2 && (
                <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                  Do this later
                </Button>
              )}
              {step < 2 ? (
                <Button
                  disabled={
                    (step === 0 && !businessReady) || (step === 1 && !ready) || save.isPending
                  }
                  onClick={next}
                >
                  {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  Continue <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  disabled={!ready || !enough || !runnerAvailable || start.isPending}
                  onClick={() => start.mutate()}
                >
                  {start.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1 h-4 w-4" />
                  )}
                  Build my website
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <TopUpDialog
        open={topUp}
        onOpenChange={setTopUp}
        wallet={credits.data}
        reason={`Building your website needs ${needed.toLocaleString()} AI credits to start. Pick a pack — purchased credits never expire.`}
      />
    </div>
  );
}
